/**
 * Cible de redirection APRÈS connexion — fonction PURE (testée), pas une expression
 * inline dans l'écran de login.
 *
 * `PrivateRoute` mémorise la page visée en poussant l'objet `Location` COMPLET du
 * routeur (`<Navigate to="/login" state={{ from: location }} />`) : `pathname`,
 * `search` ET `hash`. L'écran de connexion n'en relisait QUE le `pathname` — un
 * destinataire non connecté qui ouvrait `…/dossiers?dossier=Vacances%2F2024`
 * atterrissait après login sur `/dossiers` (racine des dossiers), et le lien partagé
 * « ne marchait pas ». Même perte pour `/?q=…`, `/?tri=…&filtre=…`, etc. — toute
 * l'adressabilité « tout dans l'URL » de la galerie tombait au premier login.
 *
 * DÉFENSIF : l'état de route vient de `history.state` (structured-cloné, survivant au
 * F5, potentiellement forgé) → chaque partie est validée indépendamment, et tout ce
 * qui n'est pas un chemin RELATIF exploitable retombe sur l'accueil. En particulier,
 * un `pathname` qui ne commence pas par `/` est rejeté : `//evil.example` ou
 * `https://evil.example` passés à `navigate()` sortiraient du site.
 */

/** Forme MINIMALE attendue de `location.state` posé par `PrivateRoute`. */
export interface RedirectFromState {
  from?: { pathname?: string; search?: string; hash?: string } | null;
}

/** Accueil : repli quand aucune cible exploitable n'est mémorisée. */
const HOME = '/';

/** Chaîne non vide, ou `null` (l'état de route n'est pas typé à l'exécution). */
function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Reconstitue `pathname + search + hash` depuis l'état de route de `PrivateRoute`.
 * Renvoie `'/'` si l'état est absent, incomplet ou non relatif.
 */
export function redirectTargetFrom(state: unknown): string {
  const from = (state as RedirectFromState | null)?.from;
  if (!from || typeof from !== 'object') return HOME;

  const pathname = str(from.pathname);
  // Chemin RELATIF au site uniquement : `/x` oui, `//host` et `http://host` non.
  if (pathname === null || !pathname.startsWith('/') || pathname.startsWith('//')) return HOME;

  // Ne jamais rebondir vers l'écran de connexion lui-même (boucle).
  if (pathname === '/login') return HOME;

  // `search`/`hash` du routeur portent déjà leur préfixe (`?…`, `#…`) ; on l'ajoute
  // s'il manque (valeur forgée) et on ignore un `?`/`#` seul (chaîne vide utile).
  const search = str(from.search);
  const hash = str(from.hash);
  const query =
    search === null || search === '?' ? '' : search.startsWith('?') ? search : `?${search}`;
  const fragment = hash === null || hash === '#' ? '' : hash.startsWith('#') ? hash : `#${hash}`;

  return `${pathname}${query}${fragment}`;
}
